import { useState, useEffect, useCallback } from 'react';
import { Spin, Tag, Button, message, Input, Modal } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { mobile } from '../../services/api';
import './mobile.css';

const STATUS_COLOR = { '待派发': 'default', '处理中': 'processing', '已完成': 'success' };
const PRIORITY_COLOR = { '低': 'blue', '中': 'orange', '高': 'red', '紧急': 'purple' };

function formatTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('zh-CN', { hour12: false });
}

export default function MobileWorkOrders() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [detail, setDetail] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [resultNote, setResultNote] = useState('');

  const loadList = useCallback(async () => {
    try {
      const res = await mobile.tasks();
      setTasks(res.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (taskId) => {
    setLoading(true);
    try {
      const { workOrders } = await import('../../services/api');
      const res = await workOrders.detail(taskId);
      setDetail(res.data);
    } catch (err) {
      message.error(err.message || '加载详情失败');
      navigate('/m/tasks');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) loadDetail(id);
    else loadList();
  }, [id, loadDetail, loadList]);

  const handleAccept = async (taskId) => {
    try {
      await mobile.acceptTask(taskId);
      message.success('接单成功');
      if (id) loadDetail(id);
      else loadList();
    } catch (err) {
      message.error(err.message || '接单失败');
    }
  };

  const handleComplete = async () => {
    try {
      await mobile.completeTask(id, { result_note: resultNote });
      message.success('工单已完成');
      setCompleteOpen(false);
      setResultNote('');
      loadDetail(id);
    } catch (err) {
      message.error(err.message || '操作失败');
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '60px auto' }} />;

  if (id && detail) {
    return (
      <div>
        <div className="mobile-card">
          <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>{detail.order_no}</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>{detail.title}</div>
          <div style={{ marginBottom: 8 }}>
            <Tag color={PRIORITY_COLOR[detail.priority]}>{detail.priority}</Tag>
            <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
          </div>
          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>{detail.description}</div>
        </div>

        <div className="mobile-card">
          <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>处理人</div>
          <div>{detail.assignee || '未分配'}</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 10 }}>创建时间</div>
          <div>{formatTime(detail.created_at)}</div>
          {detail.alarm && (
            <>
              <div style={{ fontSize: 13, color: '#888', marginTop: 10 }}>关联告警</div>
              <Tag color="red">{detail.alarm.level}</Tag> {detail.alarm.title}
            </>
          )}
        </div>

        {detail.status === '待派发' && (
          <Button type="primary" block size="large" onClick={() => handleAccept(detail.id)}>
            接单处理
          </Button>
        )}
        {detail.status === '处理中' && (
          <Button type="primary" block size="large" onClick={() => setCompleteOpen(true)}>
            完成工单
          </Button>
        )}

        <Modal
          title="完成工单"
          open={completeOpen}
          onCancel={() => setCompleteOpen(false)}
          onOk={handleComplete}
          okText="提交"
        >
          <Input.TextArea
            rows={4}
            value={resultNote}
            onChange={(e) => setResultNote(e.target.value)}
            placeholder="请填写故障原因、处理措施及恢复情况"
          />
        </Modal>
      </div>
    );
  }

  return (
    <div>
      {tasks.length === 0 && (
        <div className="mobile-card" style={{ textAlign: 'center', color: '#999', padding: 32 }}>
          暂无待处理工单
        </div>
      )}
      {tasks.map((t) => (
        <div
          key={t.id}
          className="mobile-card"
          onClick={() => navigate(`/m/tasks/${t.id}`)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Tag color={PRIORITY_COLOR[t.priority]}>{t.priority}</Tag>
            <Tag color={STATUS_COLOR[t.status]}>{t.status}</Tag>
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{t.order_no} · {t.assignee || '待接单'}</div>
        </div>
      ))}
    </div>
  );
}
